// Lógica PURA do contador de viagens. Ver `lib/rating-copy.ts` e
// `lib/badge-copy.ts` para o porquê de existir um arquivo separado: este módulo
// NÃO importa `mongodb` e não deve passar a importar. As superfícies que
// exibem o número (`/ratings/[id]`, `/users/[id]`, card da busca, detalhe da
// carona) precisam de `formatTrips` e `hasTrips`, e importar de um módulo que
// puxa o driver do Mongo faz o webpack tentar bundlar `net`/`tls`/`fs` para o
// browser. Texto e matemática ficam aqui; acesso ao banco, em
// `lib/trip-stats.ts`.
//
// CONTRASTE COM AS MÉDIAS, que é a diferença mais importante deste arquivo:
// em `rating-copy.ts` `average: null` significa "sem dado" e 0 é uma avaliação
// PÉSSIMA. Aqui não existe essa ambiguidade: uma contagem de viagens é sempre
// um número real, e `0` significa literalmente "nenhuma viagem concluída". Por
// isso não há `null` no payload, e a ausência de dado vira RÓTULO ("sem
// viagens") no componente, nunca "0 viagens" exibido como se fosse histórico.

/** Papel em que a viagem foi feita. Mesma divisão das notas (`RatingPersona`). */
export type TripPersona = "driver" | "passenger"

export interface UserTripStats {
  /** Caronas liquidadas em que a pessoa dirigiu. */
  asDriver: number
  /** Viagens em que a pessoa foi passageiro com a reserva confirmada. */
  asPassenger: number
  /** `asDriver + asPassenger`. Derivado, nunca gravado: um total gravado pode divergir. */
  total: number
}

export function emptyUserTripStats(): UserTripStats {
  return { asDriver: 0, asPassenger: 0, total: 0 }
}

/**
 * Normaliza um inteiro de contagem. `undefined`, `null`, `NaN`, fracionário e
 * negativo viram 0 — o campo é denormalizado e um valor corrompido não pode
 * virar "-3 viagens" ou "NaN viagens" na tela. `Math.trunc` porque contagem
 * fracionária é sempre erro de escrita, não arredondamento legítimo.
 */
function toCount(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0
  return Math.max(0, Math.trunc(value))
}

/**
 * Normaliza o par vindo do `User` denormalizado. `?? 0` (e não `|| 0`) não é
 * aqui uma diferença sutil como costuma ser: os dois dariam 0 para `undefined`,
 * mas o par explícito documenta que 0 é valor VÁLIDO e precisa sobreviver.
 */
export function tripsFromUserFields(driver: unknown, passenger: unknown): UserTripStats {
  const asDriver = toCount(driver)
  const asPassenger = toCount(passenger)
  return { asDriver, asPassenger, total: asDriver + asPassenger }
}

/** Se há viagem a exibir. Espelha `hasRatings` de `lib/rating-copy.ts`. */
export function hasTrips(stats: UserTripStats | null | undefined): boolean {
  return !!stats && stats.total > 0
}

/** "sem viagens" | "1 viagem" | "32 viagens". */
export function formatTrips(count: number | null | undefined): string {
  const n = toCount(count)
  if (n === 0) return "sem viagens"
  if (n === 1) return "1 viagem"
  return `${n} viagens`
}

/**
 * Quais personas têm viagem a exibir. Vive aqui, e não dentro do componente, pelo
 * motivo do `visiblePersonas` de `rating-copy.ts`: `jest.config.js` só coleta
 * `*.test.ts`, então uma decisão tomada dentro de um `.tsx` fica sem cobertura.
 * "0 viagens como motorista" para quem nunca dirigiu é ruído, não informação.
 *
 * A ordem é fixa (motorista antes de passageiro) para o resumo não trocar de
 * linha entre renders.
 */
export function visibleTripPersonas(input: {
  asDriver?: number | null
  asPassenger?: number | null
}): TripPersona[] {
  const out: TripPersona[] = []
  if (toCount(input.asDriver) > 0) out.push("driver")
  if (toCount(input.asPassenger) > 0) out.push("passenger")
  return out
}

/** Rótulo curto da persona, para as duas surfaces compartilharem o mesmo texto. */
export function tripPersonaLabel(persona: TripPersona): string {
  return persona === "driver" ? "Como motorista" : "Como passageiro"
}
